
import { Component, effect, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AgentBuilderService } from '../services/agent-builder.service';

@Component({
  selector: 'agent-definition',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
],
  templateUrl: './agent-definition.component.html',
  styleUrl: './agent-definition.component.scss'
})
export class AgentDefinitionComponent {
  agentForm!: FormGroup;

  #formBuilder = inject(FormBuilder);
  #agentBuilderService = inject(AgentBuilderService);

  constructor() {
    effect(() => {
      if (this.#agentBuilderService.agent()) {
        this.agentForm = this.#formBuilder.group({
          definition: new FormControl(this.#agentBuilderService.agent().details.definition, [Validators.required, this.#agentNameValidator]),
          systemMessage: new FormControl(this.#agentBuilderService.agent().details.systemMessage),
        });
      }
    });
  }

  /**
   * Validates the definition of the agent to make sure it is a valid JSON object.
   */
  #agentNameValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value != null) {
      try {
        let definition = JSON.parse(value);
        const regex = /^[A-Za-z0-9]+[A-Za-z0-9_]*[A-Za-z0-9]+$/gi;
        if (definition?.function?.name.match(regex) != null) {
          return null;
        } else {
          return { agentName: 'The name must start with alphanumeric, use underscores for word separation, and end in alphanumeric.' };
        }
      } catch (error) {
        return { agentName: 'Must be valid JSON.' };
      }
    }
    return null;
  }

  /**
   * Marks all controls in the agent form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the AgentBuilderComponent.
   */
  markFormAsTouched(): void {
    this.agentForm.markAllAsTouched();
  }

  /**
   * Validates the form and returns whether it's valid or not.
   * Used by the parent component to determine if this step is complete.
   * @returns A boolean indicating whether the form is valid.
   */
  isValid(): boolean {
    if (!this.agentForm) {
      return false;
    }

    // Reset form errors
    this.agentForm.get('definition')?.setErrors(null);
    
    this.agentForm.markAllAsTouched();
    
    if (this.agentForm.get('definition')?.value === '') {
      this.agentForm.get('definition')?.setErrors({ required: true });
    }
    
    return this.agentForm.valid;
  }
}

